// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview Zod schema for the connector_webhook_events VCA collection.
 *
 * Inbound webhook audit log for all Git provider events. Every raw webhook
 * delivery is persisted here before dispatch to domain handlers. This gives
 * Phase 7 idempotency (deliveryId dedup) and auditability for failed events.
 *
 * Identity tuple: (provider, deliveryId)
 * ID prefix: cwe_
 */

import { z } from 'zod'

export const connector_webhook_eventsSchema = z.object({
  id: z.string().max(100),
  orgId: z.string().max(100),
  workspaceId: z.string().max(100).optional(),

  // ── Identity tuple ────────────────────────────────────────────────────
  /** VCS provider that sent this event (e.g. 'github', 'gitlab', 'forgejo'). */
  provider: z.string().max(50),
  /**
   * Provider-assigned delivery identifier (UUID or opaque string).
   * Used for idempotency: duplicate deliveries are detected by (provider, deliveryId).
   * GitHub: X-GitHub-Delivery header. GitLab: X-Gitlab-Event-UUID.
   */
  deliveryId: z.string().max(200),

  // ── Connection reference ───────────────────────────────────────────────
  /**
   * FK → git_connections.id. Nullable because the handler may fail to match
   * the incoming event to a known connection (e.g. unregistered installation).
   */
  gitConnectionId: z.string().max(100).nullable().optional(),

  // ── Event classification ──────────────────────────────────────────────
  /** Provider event type (e.g. 'push', 'pull_request', 'workflow_run'). */
  eventType: z.string().max(200),

  // ── Raw event data ────────────────────────────────────────────────────
  /** Full deserialized payload from the provider. */
  payload: z.record(z.string(), z.unknown()),
  /** Relevant HTTP headers (signature, delivery ID, content-type). */
  headers: z.record(z.string(), z.unknown()),

  // ── Processing lifecycle ──────────────────────────────────────────────
  receivedAt: z.string().datetime(),
  /** Set when processing finishes (success or terminal failure). */
  processedAt: z.string().datetime().nullable().optional(),
  /** Processing state machine. */
  status: z.enum(['received', 'processed', 'failed']),
  /** Human-readable error message when status === 'failed'. */
  errorMessage: z.string().nullable().optional(),
})

export type Connector_webhook_eventsInput = z.infer<typeof connector_webhook_eventsSchema>
